// src/components/ClientAdminManager.jsx
import React, { useState, useEffect, useRef } from 'react';
import '../styles/ClientManager.css';
import '../styles/fleet-theme.css';
import { apiFetchJSON } from '../api/apiClient';
import { FieldError, getConflictField, invalidInputStyle } from './fieldErrors';
import FleetHeader from './FleetHeader';
import { usePageTour } from './tour/tourStore';

// Lives in public/, not src/images/ - needed for a stable, non-hashed
// URL the index.html preload tag can target.
const loadingVideo = process.env.PUBLIC_URL + '/Logo_video.mp4';

function ClientAdminManager({ user, onLogout }) {
    const [clients, setClients] = useState([]);
    // This client's sites (each with .devices, mixing Peplink + Starlink) -
    // mirrors sitesByClient in the admin-side ClientManager.jsx, but scoped
    // server-side to just the logged-in client admin's own client, since
    // devices reach a client via site linkage rather than direct assignment.
    const [sites, setSites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [sendingReset, setSendingReset] = useState(false);

    useEffect(() => {
      const handleResize = () => {
        setIsMobile(window.innerWidth <= 768);
      };

      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    const clientAdminUser = user;

    // Form states - REMOVED password field
    const [newUser, setNewUser] = useState({
        username: '',
        full_name: '',
        email: '',
        role: 'viewer'
    });
    
    const [editingUser, setEditingUser] = useState(null);
    const [resettingPassword, setResettingPassword] = useState(null);
    
    // UI states
    const [showNewUserForm, setShowNewUserForm] = useState(false);
    const [showVerificationInfo, setShowVerificationInfo] = useState(null); // ADD THIS
    const [expandedClient, setExpandedClient] = useState(null);
    const [message, setMessage] = useState({ text: '', type: '' });
    // Errors shown under a form field, e.g. { email: 'This email is already used...' }
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        fetchClientData();
    }, []);

    // Start every add/edit form without leftover field errors.
    useEffect(() => {
        setFieldErrors({});
    }, [showNewUserForm, editingUser?.user_id]);

    const clearFieldError = (field) =>
        setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));

    const formatDateTime = (timestamp) => {
        if (!timestamp) return 'Unknown';
        
        let date;
        
        if (typeof timestamp === 'number') {
            date = new Date(timestamp * 1000);
        } else {
            date = new Date(timestamp);
        }
        
        if (isNaN(date.getTime())) return 'Invalid date';
        
        const day = date.getDate();
        const month = date.toLocaleDateString('en-US', { month: 'short' });
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        
        return `${day} ${month} ${year} ${hours}:${minutes}`;
    };

    const showMessage = (text, type = 'success') => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };

    const fetchClientData = async () => {
        try {
            setLoading(true);
            
            const [clientResponse, usersResponse, sitesResponse] = await Promise.all([
                apiFetchJSON('/api/client/client-info'),
                apiFetchJSON('/api/client/users'),
                // Existing client-scoped, site-based endpoint (sites/routes.py) -
                // already returns Peplink + Starlink together, already scoped to
                // this client's own linked sites via sites_for_client(). No new
                // backend route needed; this was previously pointed at a
                // not-yet-existing /api/client/sites placeholder.
                apiFetchJSON('/api/v2/client/dashboard?grouped=true')
            ]);
            
            const clientData = {
                ...clientResponse,
                users: usersResponse.users || []
            };
            
            setClients([clientData]);
            setSites(sitesResponse.sites || []);
            setLastUpdated(formatDateTime(new Date()));
            
        } catch (error) {
            console.error('Error fetching client data:', error);
            showMessage('Failed to load client data: ' + error.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const getDefaultUsernamePrefix = (clientName) => {
        if (!clientName) return '';
        
        const prefix = clientName.toLowerCase()
            .replace(/\s+/g, '.')
            .replace(/[^a-z0-9.-]/g, '');
        
        return `${prefix}.`;
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        if (!newUser.username || !newUser.email) {
            showMessage('Username and email are required', 'error');
            return;
        }
        
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newUser.email)) {
            showMessage('Please enter a valid email address', 'error');
            return;
        }
        
        try {
            setLoading(true);
            
            // Using client admin endpoint
            await apiFetchJSON('/api/client/add-user', {
                method: "POST",
                body: JSON.stringify(newUser)
            });

            // Show success message (no password shown)
            showMessage(
                `User "${newUser.username}" created! Setup email sent to ${newUser.email}`,
                'success'
            );
            
            // Show info modal about email verification
            setShowVerificationInfo({
                email: newUser.email,
                username: newUser.username
            });
                
            // Reset form
            setNewUser({
                username: '',
                full_name: '',
                email: '',
                role: 'viewer'
            });
                
            setShowNewUserForm(false);
            fetchClientData();

            
        } catch (error) {
            const errorMessage = error.message || 'Failed to add user';
            const field = getConflictField(error);
            if (field) {
                // Show it under the field - the banner is hidden behind the form
                setFieldErrors({ [field]: errorMessage });
            } else {
                showMessage(errorMessage, 'error');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEditUser = async () => {
        if (!editingUser) return;
        
        try {
            await apiFetchJSON(`/api/client/update-user/${editingUser.user_id}`, {
                method: "PUT",
                body: JSON.stringify({
                    full_name: editingUser.full_name,
                    email: editingUser.email,
                    role: editingUser.role,
                    username: editingUser.username
                })
            });
            
            showMessage('User updated successfully!');
            setEditingUser(null);
            fetchClientData();
        } catch (error) {
            const errorMessage = error.message || 'Failed to update user';
            if (getConflictField(error) === 'email') {
                setFieldErrors({ email: errorMessage });
            } else {
                showMessage(errorMessage, 'error');
            }
        }
    };

    const handleDeleteUser = async (userId, username) => {
        if (!window.confirm(`Are you sure you want to delete user "${username}"?`)) {
            return;
        }
        
        try {
            await apiFetchJSON(`/api/client/delete-user/${userId}`, {
                method: "DELETE"
            });
            showMessage('User deleted successfully!');
            fetchClientData();
        } catch (error) {
            showMessage(error.message || 'Failed to delete user', 'error');
        }
    };

    const handleSendResetEmail = async () => {
        if (!resettingPassword) return;
        
        setSendingReset(true);
        try {
            const response = await apiFetchJSON('/api/admin/client-management/send-reset-email', {
                method: "POST",
                body: JSON.stringify({
                    user_id: resettingPassword.user_id,
                    client_id: resettingPassword.client_id
                })
            });
            
            if (response.success) {
                showMessage(`Password reset email sent to ${resettingPassword.email}`, 'success');
                setResettingPassword(null);
            } else {
                showMessage(response.error || 'Failed to send password reset email', 'error');
            }
        } catch (error) {
            showMessage(error.message || 'Failed to send password reset email', 'error');
        } finally {
            setSendingReset(false);
        }
    };

    const handleToggleUserStatus = async (userId, username, currentStatus) => {
        const newStatus = !currentStatus;
        const action = newStatus ? 'activate' : 'deactivate';
        
        if (!window.confirm(`Are you sure you want to ${action} user "${username}"?`)) {
            return;
        }
        
        try {
            await apiFetchJSON(`/api/client/toggle-user-status/${userId}`, {
                method: "PUT",
                body: JSON.stringify({
                    is_active: newStatus
                })
            });
            
            showMessage(`User "${username}" ${action}d successfully!`);
            fetchClientData();
        } catch (error) {
            showMessage(error.message || `Failed to ${action} user`, 'error');
        }
    };

    // Per-account push switch. Only operator/admin roles are ever sent push
    // alerts by the server, so viewers get a disabled button.
    const handleToggleUserPush = async (user) => {
        const enable = !user.push_enabled;
        if (!enable && !window.confirm(
            `Turn off push notifications for "${user.username}"? All of their devices will stop receiving alerts until turned back on.`
        )) return;
        try {
            await apiFetchJSON(`/api/client/users/${user.user_id}/push`, {
                method: 'PUT',
                body: JSON.stringify({ push_enabled: enable })
            });
            showMessage(`Push notifications ${enable ? 'enabled' : 'disabled'} for "${user.username}"`);
            fetchClientData();
        } catch (error) {
            showMessage(error.message || 'Failed to update push notifications', 'error');
        }
    };

    // Lost phone: turn off the user's authenticator app (back to email codes)
    const handleReset2fa = async (user) => {
        if (!window.confirm(
            `Reset 2FA for "${user.username}"? Their authenticator app and backup codes will stop working and login codes will go to their email instead.`
        )) return;
        try {
            await apiFetchJSON(`/api/client/users/${user.user_id}/reset-2fa`, { method: 'POST' });
            showMessage(`2FA reset for "${user.username}"`);
            fetchClientData();
        } catch (error) {
            showMessage(error.message || 'Failed to reset 2FA', 'error');
        }
    };

    const pushTitle = (user) =>
        user.role === 'viewer'
            ? 'Viewers do not receive push alerts'
            : (user.push_enabled ? 'Push notifications are on - click to turn off' : 'Push notifications are off - click to turn on');

    const toggleClientExpansion = (clientId) => {
        setExpandedClient(expandedClient === clientId ? null : clientId);
    };

    const handleBackToDashboard = () => {
        window.location.href = '/dashboard';
    };

    // Update newUser when form opens
    const updateUsername = () => {
        const clientName = clients.length > 0 ? clients[0].client_name : '';
        const prefix = getDefaultUsernamePrefix(clientName);
        setNewUser(prev => ({
            ...prev,
            username: prefix,
            usernameSuffix: ''
        }));
    };

    // Every device on this client's linked site(s), de-duplicated by
    // device_type + device_key - the single source for the DEVICES counter
    // and the device list (same rule as ClientManager's getClientDevices).
    const clientDevices = (() => {
        const seen = new Set();
        const devices = [];
        sites.forEach((site) => {
            (site.devices || []).forEach((d) => {
                const key = d.device_type + ':' + d.device_key;
                if (seen.has(key)) return;
                seen.add(key);
                devices.push({ ...d, site_name: site.site_name });
            });
        });
        return devices;
    })();
    const deviceCount = clientDevices.length;

    // ---- Guided tour ("?" in FleetHeader) ---------------------------------
    const tourCompany = clients[0] || null;
    const tourExpandedBeforeRef = useRef(null);
    usePageTour('user-manager', {
        isMobile,
        company: tourCompany,
        deviceCount,
        myUsername: clientAdminUser?.username,
        expandCompany: () => tourCompany && setExpandedClient(tourCompany.client_id),
        onTourStart: () => { tourExpandedBeforeRef.current = expandedClient; },
        onTourEnd: () => setExpandedClient(tourExpandedBeforeRef.current),
    }, { ready: !loading });

    if (loading) return (
        <div className="cm-loading-overlay">
          <div className="cm-spinner-container">
            <video autoPlay loop muted playsInline preload="auto" poster={process.env.PUBLIC_URL + "/Logo_video_poster.jpg"} className="spinner-video" width="280" height="280">
              <source src={loadingVideo} type="video/mp4" />
            </video>
            <p>Loading user data…</p>
          </div>
        </div>
    );

    return (
        <div className="App cm-client-manager">
        <FleetHeader
            title={clients.length > 0 ? `Users - ${clients[0].client_name}` : 'Users'}
            user={user}
            onLogout={onLogout}
            scope="client"
        />

            {/* Main Container */}
            <div className="cm-dashboard-container">
                {/* Message Display */}
                {message.text && (
                    <div className={`ft-toolbar-${message.type === 'error' ? 'error' : 'success'}`} role="alert">
                        <i className={`fas fa-${message.type === 'error' ? 'exclamation-triangle' : 'check-circle'} me-2`}></i>
                        {message.text}
                        <button type="button" className="btn-close" onClick={() => setMessage({ text: '', type: '' })}></button>
                    </div>
                )}

                {/* Clients List */}
                <div className="cm-panel">
                    <div className="cm-tabs-row">
                        <span className="cm-tab cm-active">CLIENT INFORMATION</span>
                    </div>
                    <div className="cm-panel-body">
                        {clients.length === 0 ? (
                            <div className="cm-text-center cm-py-4">
                                <div className="cm-empty-state">
                                    <h5>Client information not found</h5>
                                    <p className="cm-text-muted">Please contact support</p>
                                </div>
                            </div>
                        ) : isMobile ? (
                                        <div className="cm-client-mobile-list">
                                            {clients.map(client => (
                                                <div key={client.client_id} className="cm-client-mobile-card" data-tour={tourCompany && client.client_id === tourCompany.client_id ? 'um-company' : undefined}>
                                                    <div className="cmc-header">
                                                        <div className="cmc-name">
                                                            {client.client_name}
                                                        </div>
                                                        <div className={
                                                            'cmc-status cmc-status-' +
                                                            (client.is_active ? 'active' : client.is_verified === false ? 'pending' : 'inactive')
                                                        }>
                                                            <span className="cmc-status-dot" />
                                                            {client.is_active ? 'Active' : client.is_verified === false ? 'Pending' : 'Inactive'}
                                                        </div>
                                                    </div>

                                                    <div className="cmc-subrow">
                                                        <span className="cmc-email">{client.contact_email || '-'}</span>
                                                    </div>

                                                    <div className="cmc-divider" />

                                                    <div className="cmc-stats">
                                                        <div className="cmc-stat">
                                                            {client.user_count || 0} Users
                                                        </div>
                                                        <div className="cmc-stat">
                                                            {deviceCount} {deviceCount === 1 ? 'Device' : 'Devices'}
                                                        </div>
                                                        <div className="cmc-stat cmc-stat-muted">
                                                            {new Date(client.created_at).toLocaleDateString()}
                                                        </div>
                                                    </div>

                                                    <div className="cmc-actions" data-tour={tourCompany && client.client_id === tourCompany.client_id ? 'um-actions' : undefined}>
                                                        <button
                                                            className="cmc-action-label"
                                                            onClick={() => {
                                                                updateUsername();
                                                                setShowNewUserForm(true);
                                                            }}
                                                            title="Add User"
                                                        >
                                                            + USER
                                                        </button>
                                                        <button
                                                            className="cmc-action-label cmc-action-muted"
                                                            onClick={() => toggleClientExpansion(client.client_id)}
                                                            title={expandedClient === client.client_id ? 'Collapse' : 'Expand'}
                                                        >
                                                            {expandedClient === client.client_id ? 'Collapse' : 'Expand'}
                                                        </button>
                                                    </div>

                                                    {expandedClient === client.client_id && (
                                                        <div className="cmc-expanded" data-tour="um-detail">
                                                            {client.users && client.users.length > 0 ? (
                                                                <div className="cmc-expanded-section">
                                                                    <div className="cmc-expanded-title">
                                                                        Users ({client.users.length})
                                                                    </div>
                                                                    <div className="cmc-user-list">
                                                                        {client.users.map(user => (
                                                                            <div key={user.user_id} className="cmc-user-row">
                                                                                <div className="cmc-user-info">
                                                                                    <div className="cmc-user-name">{user.full_name || user.username}</div>
                                                                                    <div className="cmc-user-meta">
                                                                                        <span className={`cmc-user-role cmc-user-role-${user.role}`}>
                                                                                            {user.role === 'admin' ? 'Admin' : user.role === 'operator' ? 'Operator' : 'Viewer'}
                                                                                        </span>
                                                                                        {user.email || '-'}
                                                                                    </div>
                                                                                    <div className="cmc-user-status">
                                                                                        {!user.is_verified ? 'Pending Verification' : user.is_active ? 'Active' : 'Inactive'}
                                                                                        {user.last_login ? ` · Last login: ${new Date(user.last_login).toLocaleString()}` : ' · Never logged in'}
                                                                                    </div>
                                                                                </div>
                                                                                <div className="cmc-user-actions">
                                                                                    <button
                                                                                        className="cmc-action-label"
                                                                                        disabled={user.username === clientAdminUser?.username}
                                                                                        onClick={() => setEditingUser({
                                                                                            user_id: user.user_id,
                                                                                            username: user.username,
                                                                                            full_name: user.full_name || '',
                                                                                            email: user.email || '',
                                                                                            role: user.role
                                                                                        })}
                                                                                        title="Edit User"
                                                                                    >
                                                                                        EDIT
                                                                                    </button>
                                                                                    <button
                                                                                        className="cmc-action-label"
                                                                                        onClick={() => setResettingPassword({
                                                                                            user_id: user.user_id,
                                                                                            username: user.username,
                                                                                            email: user.email,
                                                                                            client_id: clients[0]?.client_id
                                                                                        })}
                                                                                        title="Send Password Reset Email"
                                                                                    >
                                                                                        RESET PW
                                                                                    </button>
                                                                                    <button
                                                                                        className={'cmc-action-label cmc-action-danger' + (user.push_enabled && user.role !== 'viewer' ? '' : ' cmc-action-muted')}
                                                                                        disabled={user.role === 'viewer'}
                                                                                        onClick={() => handleToggleUserPush(user)}
                                                                                        title={pushTitle(user)}
                                                                                    >
                                                                                        {user.role === 'viewer' ? 'NOTIF N/A' : (user.push_enabled ? 'NOTIF OFF' : 'NOTIF ON')}
                                                                                    </button>
                                                                                    {user.totp_enabled && user.username !== clientAdminUser?.username && (
                                                                                        <button
                                                                                            className="cmc-action-label"
                                                                                            onClick={() => handleReset2fa(user)}
                                                                                            title="Authenticator app is on - reset it if they lost their phone"
                                                                                        >
                                                                                            RESET 2FA
                                                                                        </button>
                                                                                    )}
                                                                                    <button
                                                                                        className="cmc-action-label"
                                                                                        disabled={user.username === clientAdminUser?.username || !user.is_verified}
                                                                                        onClick={() => {
                                                                                            if (!user.is_verified) {
                                                                                                showMessage('User must verify email first', 'warning');
                                                                                                return;
                                                                                            }
                                                                                            handleToggleUserStatus(user.user_id, user.username, user.is_active);
                                                                                        }}
                                                                                        title={!user.is_verified ? 'Must verify email first' : (user.is_active ? 'Deactivate' : 'Activate')}
                                                                                    >
                                                                                        {!user.is_verified ? 'PENDING' : (user.is_active ? 'DEACTIVATE' : 'ACTIVATE')}
                                                                                    </button>
                                                                                    <button
                                                                                        className="cmc-action-label cmc-action-danger"
                                                                                        disabled={user.username === clientAdminUser?.username}
                                                                                        onClick={() => handleDeleteUser(user.user_id, user.username)}
                                                                                        title="Delete User"
                                                                                    >
                                                                                        DELETE
                                                                                    </button>
                                                                                </div>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="cmc-no-users">No users yet</div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                    <div className="cm-grid-wrap">
                                        <div className="cm-grid-header">
                                            <div>CLIENT</div>
                                            <div>CONTACT EMAIL</div>
                                            <div>STATUS</div>
                                            <div className="cm-num">DEVICES</div>
                                            <div className="cm-num">USERS</div>
                                            <div>CREATED</div>
                                            <div className="cm-num">ACTIONS</div>
                                        </div>
                                        {clients.map((client, idx) => (
                                            <React.Fragment key={client.client_id}>
                                            <div className={'cm-grid-row' + (idx % 2 === 1 ? ' cm-grid-row-alt' : '')} data-tour={tourCompany && client.client_id === tourCompany.client_id ? 'um-company' : undefined}>
                                                <div>
                                                    <div className="cm-client-name">{client.client_name}</div>
                                                </div>
                                                <div className="cm-email">{client.contact_email || '-'}</div>
                                                <div>
                                                    {client.is_active ? (
                                                        <span className="cm-status-chip cm-active">ACTIVE</span>
                                                    ) : client.is_verified === false ? (
                                                        <span className="cm-status-chip cm-pending">PENDING</span>
                                                    ) : (
                                                        <span className="cm-status-chip cm-inactive">INACTIVE</span>
                                                    )}
                                                </div>
                                                <div className={'cm-num cm-mono' + (deviceCount === 0 ? ' cm-dim' : '')}>
                                                    {deviceCount}
                                                </div>
                                                <div className="cm-num cm-mono cm-accent">
                                                    {client.user_count || 0}
                                                </div>
                                                <div className="cm-created">
                                                    {new Date(client.created_at).toLocaleDateString()}
                                                </div>
                                                <div className="cm-row-actions" data-tour={tourCompany && client.client_id === tourCompany.client_id ? 'um-actions' : undefined}>
                                                    <button
                                                        className="cm-action"
                                                        onClick={() => {
                                                            updateUsername();
                                                            setShowNewUserForm(true);
                                                        }}
                                                        title="Add User"
                                                    >
                                                        + USER
                                                    </button>
                                                    <button
                                                        className="cm-action"
                                                        onClick={() => toggleClientExpansion(client.client_id)}
                                                        title={expandedClient === client.client_id ? "Collapse" : "Expand"}
                                                    >
                                                        {expandedClient === client.client_id ? 'COLLAPSE ▲' : 'EXPAND ▼'}
                                                    </button>
                                                </div>
                                            </div>
                                            {expandedClient === client.client_id && (
                                                <div className="cm-grid-detail-row" data-tour="um-detail">
                                                    <div className="cm-client-details">
                                                        {/* Devices Section - via linked sites, not direct assignment.
                                                            Mirrors the admin-side ClientManager.jsx, reading from
                                                            /api/v2/client/dashboard?grouped=true (server-scoped to
                                                            this client's own linked sites via sites_for_client())
                                                            instead of the old Peplink-only /api/client/routers.
                                                            Unlike the admin view, this doesn't link out to Site
                                                            Manager, since client admins don't have access to it. */}
                                                        {(() => {
                                                            const allDevices = clientDevices;
                                                            const peplinkCount = allDevices.filter((d) => d.device_type === 'peplink').length;
                                                            const starlinkCount = allDevices.filter((d) => d.device_type === 'starlink').length;

                                                            if (allDevices.length === 0) {
                                                                return (
                                                                    <div className="cm-detail-section">
                                                                        <div className="cm-detail-head">
                                                                            <span>DEVICES</span>
                                                                            <span className="cm-detail-count">0</span>
                                                                        </div>
                                                                        <small className="cm-text-muted">
                                                                            No devices yet.
                                                                        </small>
                                                                    </div>
                                                                );
                                                            }

                                                            return (
                                                                <div className="cm-detail-section">
                                                                    <div className="cm-detail-head">
                                                                        <span>DEVICES</span>
                                                                        <span className="cm-detail-count">{allDevices.length} — {peplinkCount} Peplink · {starlinkCount} Starlink</span>
                                                                    </div>
                                                                    {allDevices.map((d) => (
                                                                        <div key={d.device_type + d.device_key} className="cm-detail-row">
                                                                            <span className="cm-detail-primary">{d.name || d.device_key}</span>
                                                                            <span className={'cm-kind cm-' + d.device_type}>
                                                                                {d.device_type === 'peplink' ? 'PEPLINK' : 'STARLINK'}
                                                                            </span>
                                                                            {d.source === 'roam' && <span className="cm-roam">ROAM</span>}
                                                                            <span className="cm-detail-meta">{d.site_name}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            );
                                                        })()}
                                                        
                                                        {/* Users Section */}
                                                        {client.users && client.users.length > 0 && (
                                                            <div className="cm-detail-section">
                                                                <div className="cm-detail-head">
                                                                    <span>USERS</span>
                                                                    <span className="cm-detail-count">{client.users.length}</span>
                                                                </div>
                                                                {client.users.map(user => (
                                                                    <div key={user.user_id} className="cm-user-row">
                                                                        <span className="cm-detail-primary">{user.username}</span>
                                                                        <span className="cm-user-fullname">{user.full_name || '-'}</span>
                                                                        <span className={'cm-role-chip cm-' + user.role}>
                                                                            {user.role === 'admin' ? 'ADMIN' :
                                                                            user.role === 'operator' ? 'OPERATOR' : 'VIEWER'}
                                                                        </span>
                                                                        <div className="cm-row-actions">
                                                                            <button
                                                                                className="cm-action"
                                                                                onClick={() => {
                                                                                    setEditingUser({
                                                                                        user_id: user.user_id,
                                                                                        username: user.username,
                                                                                        full_name: user.full_name || '',
                                                                                        email: user.email || '',
                                                                                        role: user.role
                                                                                    });
                                                                                }}
                                                                                title="Edit User"
                                                                                disabled={user.username === clientAdminUser?.username}
                                                                            >
                                                                                EDIT
                                                                            </button>
                                                                            <button
                                                                                className="cm-action"
                                                                                onClick={() => {
                                                                                    setResettingPassword({
                                                                                        user_id: user.user_id,
                                                                                        username: user.username,
                                                                                        email: user.email,
                                                                                        client_id: clients[0]?.client_id
                                                                                    });
                                                                                }}
                                                                                title="Send Password Reset Email"
                                                                            >
                                                                                RESET PW
                                                                            </button>
                                                                            <button
                                                                                className={'cm-action cm-action-danger' + (user.push_enabled && user.role !== 'viewer' ? '' : ' cm-action-muted')}
                                                                                onClick={() => handleToggleUserPush(user)}
                                                                                title={pushTitle(user)}
                                                                                disabled={user.role === 'viewer'}
                                                                            >
                                                                                {user.role === 'viewer' ? 'NOTIF N/A' : (user.push_enabled ? 'NOTIF OFF' : 'NOTIF ON')}
                                                                            </button>
                                                                            {user.totp_enabled && user.username !== clientAdminUser?.username && (
                                                                                <button
                                                                                    className="cm-action"
                                                                                    onClick={() => handleReset2fa(user)}
                                                                                    title="Authenticator app is on - reset it if they lost their phone"
                                                                                >
                                                                                    RESET 2FA
                                                                                </button>
                                                                            )}
                                                                            <button
                                                                                className="cm-action cm-action-muted"
                                                                                onClick={() => {
                                                                                    if (!user.is_verified) {
                                                                                        showMessage('User must verify email first', 'warning');
                                                                                        return;
                                                                                    }
                                                                                    handleToggleUserStatus(user.user_id, user.username, user.is_active);
                                                                                }}
                                                                                title={
                                                                                    !user.is_verified 
                                                                                        ? 'Must verify email first' 
                                                                                        : (user.is_active ? 'Deactivate' : 'Activate')
                                                                                }
                                                                                disabled={user.username === clientAdminUser?.username || !user.is_verified}
                                                                            >
                                                                                {!user.is_verified ? 'PENDING' : (user.is_active ? 'DEACTIVATE' : 'ACTIVATE')}
                                                                            </button>
                                                                            <button
                                                                                className="cm-action cm-action-muted"
                                                                                onClick={() => handleDeleteUser(user.user_id, user.username)}
                                                                                title="Delete User"
                                                                                disabled={user.username === clientAdminUser?.username}
                                                                            >
                                                                                DELETE
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        
                                                        {deviceCount === 0 && 
                                                         (!client.users || client.users.length === 0) && (
                                                            <div className="cm-text-center cm-py-4">
                                                                <div className="cm-empty-state">
                                                                    <h6>No additional details</h6>
                                                                    <p className="cm-text-muted cm-small">No devices or users added</p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                    )}
                                </div>
                            </div>
                </div>

            {/* Add User Modal - UPDATED: Removed password field, added email requirement */}
            {showNewUserForm && (
                <div className="cm-modal-overlay">
                    <div className="cm-modal-content">
                        <div className="cm-modal-header">
                            <h5 className="cm-modal-title">
                                ADD USER
                                {clients.length > 0 && (
                                    <>
                                        <span className="cm-modal-title-sep">/</span>
                                        <span className="cm-modal-title-entity">{clients[0].client_name}</span>
                                    </>
                                )}
                            </h5>
                            <button 
                                type="button" 
                                className="cm-btn-close" 
                                onClick={() => setShowNewUserForm(false)}
                            ></button>
                        </div>
                        <form onSubmit={handleAddUser}>
                            <div className="cm-client-manager-modal-body">
                                <div className="cm-alert cm-alert-info"> Adding user to : <strong>{clients.length > 0 ? clients[0].client_name : ''}</strong>
                                </div>
                                
                                <div className="cm-row cm-mt-3">
                                    <div className="cm-col-md-6">
                                        <div className="cm-form-group">
                                            <label className="cm-form-label">Username *</label>
                                            <div className="cm-input-group">
                                                <span className="input-group-text cm-username-prefix">
                                                    {clients.length > 0 ? 
                                                        getDefaultUsernamePrefix(clients[0].client_name) : 
                                                        ''
                                                    }
                                                </span>
                                                <input
                                                    type="text"
                                                    className="cm-form-control"
                                                    value={newUser.usernameSuffix || ''}
                                                    style={fieldErrors.username ? invalidInputStyle : undefined}
                                                    aria-invalid={!!fieldErrors.username}
                                                    onChange={(e) => {
                                                        clearFieldError('username');
                                                        const prefix = clients.length > 0 ? 
                                                            getDefaultUsernamePrefix(clients[0].client_name) : 
                                                            '';
                                                        setNewUser({
                                                            ...newUser,
                                                            usernameSuffix: e.target.value,
                                                            username: prefix + e.target.value
                                                        });
                                                    }}
                                                    required
                                                    placeholder="username"
                                                />
                                            </div>
                                            <FieldError message={fieldErrors.username} />
                                            <div className="cm-form-text">
                                                Full username: <code>{newUser.username || ''}</code>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="cm-col-md-6">
                                        <div className="cm-form-group">
                                            <label className="cm-form-label">Email *</label>
                                            <input
                                                type="email"
                                                className="cm-form-control"
                                                value={newUser.email}
                                                style={fieldErrors.email ? invalidInputStyle : undefined}
                                                aria-invalid={!!fieldErrors.email}
                                                onChange={(e) => {
                                                    clearFieldError('email');
                                                    setNewUser({...newUser, email: e.target.value});
                                                }}
                                                required
                                                placeholder="user@example.com"
                                            />
                                            <FieldError message={fieldErrors.email} />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="cm-row cm-mt-2">
                                    <div className="cm-col-md-6">
                                        <div className="cm-form-group">
                                            <label className="cm-form-label">Full Name</label>
                                            <input
                                                type="text"
                                                className="cm-form-control"
                                                value={newUser.full_name}
                                                onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                                                placeholder="John Doe"
                                            />
                                        </div>
                                    </div>
                                    <div className="cm-col-md-6">
                                        <div className="cm-form-group">
                                            <label className="cm-form-label">Role</label>
                                            <select
                                                className="cm-form-control"
                                                value={newUser.role}
                                                onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                                            >
                                                <option value="viewer">Viewer (Read-only)</option>
                                                <option value="operator">Operator (Limited actions)</option>
                                                <option value="admin">Admin (Full access within client)</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Note about email setup */}
                                <div className="cm-alert cm-alert-info cm-mt-3"> The user will receive an email to set their own password.
                                </div>
                            </div>
                            <div className="cm-modal-footer">
                                <button 
                                    type="button" 
                                    className="cm-btn cm-btn-secondary" 
                                    onClick={() => setShowNewUserForm(false)}
                                >
                                    CANCEL
                                </button>
                                <button 
                                    type="submit" 
                                    className="cm-btn cm-btn-primary"
                                    disabled={!newUser.username || !newUser.email}
                                >
                                    CREATE USER
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit User Modal - keep as is */}
            {editingUser && (
                <div className="cm-modal-overlay">
                    <div className="cm-modal-content">
                        <div className="cm-modal-header">
                            <h5 className="cm-modal-title">
                                EDIT USER
                                <span className="cm-modal-title-sep">/</span>
                                <span className="cm-modal-title-entity">{editingUser.username}</span>
                            </h5>
                            <button 
                                type="button" 
                                className="cm-btn-close" 
                                onClick={() => setEditingUser(null)}
                            ></button>
                        </div>
                        <div className="cm-client-manager-modal-body">
                            <div className="cm-row cm-mt-3">
                                <div className="cm-col-md-6">
                                    <div className="cm-form-group">
                                        <label className="cm-form-label">Full Name</label>
                                        <input
                                            type="text"
                                            className="cm-form-control"
                                            value={editingUser.full_name}
                                            onChange={(e) => setEditingUser({
                                                ...editingUser,
                                                full_name: e.target.value
                                            })}
                                            placeholder="John Doe"
                                        />
                                    </div>
                                </div>
                                <div className="cm-col-md-6">
                                    <div className="cm-form-group">
                                        <label className="cm-form-label">Email</label>
                                        <input
                                            type="email"
                                            className="cm-form-control"
                                            value={editingUser.email}
                                            style={fieldErrors.email ? invalidInputStyle : undefined}
                                            aria-invalid={!!fieldErrors.email}
                                            onChange={(e) => {
                                                clearFieldError('email');
                                                setEditingUser({
                                                    ...editingUser,
                                                    email: e.target.value
                                                });
                                            }}
                                            placeholder="john@example.com"
                                        />
                                        <FieldError message={fieldErrors.email} />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="cm-form-group cm-mt-3">
                                <label className="cm-form-label">Role</label>
                                <select
                                    className="cm-form-control"
                                    value={editingUser.role}
                                    onChange={(e) => setEditingUser({
                                        ...editingUser,
                                        role: e.target.value
                                    })}
                                >
                                    <option value="viewer">Viewer (Read-only)</option>
                                    <option value="operator">Operator (Limited actions)</option>
                                    <option value="admin">Admin (Full access within client)</option>
                                </select>
                            </div>
                        </div>
                        <div className="cm-modal-footer">
                            <button 
                                type="button" 
                                className="cm-btn cm-btn-secondary" 
                                onClick={() => setEditingUser(null)}
                            >
                                CANCEL
                            </button>
                            <button 
                                type="button" 
                                className="cm-btn cm-btn-primary"
                                onClick={handleEditUser}
                            >
                                SAVE CHANGES
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Send Reset Email Modal */}
            {resettingPassword && resettingPassword.email && !resettingPassword.new_password && (
                <div className="cm-modal-overlay">
                    <div className="cm-modal-content">
                        <div className="cm-modal-header">
                            <h5 className="cm-modal-title">
                                RESET PASSWORD
                                <span className="cm-modal-title-sep">/</span>
                                <span className="cm-modal-title-entity">{resettingPassword.username}</span>
                            </h5>
                            <button 
                                type="button" 
                                className="cm-btn-close" 
                                onClick={() => setResettingPassword(null)}
                            ></button>
                        </div>
                        <div className="cm-client-manager-modal-body">
                            <div className="cm-alert cm-alert-info"> You are sending a password reset email to :
                            </div>
                            
                            <div className="reset-email-info cm-mb-3">
                                <div><strong>User :</strong> {resettingPassword.username}</div>
                                <div><strong>Email :</strong> {resettingPassword.email}</div>
                            </div>
                            
                            <div className="cm-alert cm-alert-warning"> The user will receive an email with a link to set their own password.
                            </div>
                        </div>
                        <div className="cm-modal-footer">
                            <button 
                                type="button" 
                                className="cm-btn cm-btn-secondary" 
                                onClick={() => setResettingPassword(null)}
                            >
                                CANCEL
                            </button>
                            <button 
                                type="button" 
                                className="cm-btn cm-btn-primary"
                                onClick={handleSendResetEmail}
                                disabled={sendingReset}
                            >
                                {sendingReset ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin cm-me-2"></i>
                                        Sending...
                                    </>
                                ) : (
                                    <> Send Password Reset Email
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Initial Credentials Modal - REMOVED */}

            {/* Verification Info Modal - ADD THIS */}
            {showVerificationInfo && (
                <div className="cm-modal-overlay">
                    <div className="cm-modal-content">
                        <div className="cm-modal-header">
                            <h5 className="cm-modal-title">
                                VERIFICATION SENT
                                <span className="cm-modal-title-sep">/</span>
                                <span className="cm-modal-title-entity">{showVerificationInfo.username}</span>
                            </h5>
                            <button 
                                type="button" 
                                className="cm-btn-close" 
                                onClick={() => setShowVerificationInfo(null)}
                            ></button>
                        </div>
                        <div className="cm-client-manager-modal-body">
                            <div className="ft-toolbar-success" role="status">
                                <i className="fas fa-check-circle me-2"></i>
                                User created successfully!
                            </div>
                            
                            <div className="cm-verification-info">
                                <p>A verification email has been sent to :</p>
                                <div className="cm-email-highlight">
                                    <strong>{showVerificationInfo.email}</strong>
                                </div>
                                
                                <div className="cm-mt-3">
                                    <p>The user will need to :</p>
                                    <ol className="cm-verification-steps">
                                        <li>Click the verification link in the email</li>
                                        <li>Set their initial password</li>
                                        <li>Access their dashboard</li>
                                    </ol>
                                </div>
                                
                                <div className="cm-alert cm-alert-warning cm-mt-3"> The user account will remain inactive until verified.
                                </div>
                            </div>
                        </div>
                        <div className="cm-modal-footer">
                            <button 
                                className="cm-btn cm-btn-primary"
                                onClick={() => {
                                    navigator.clipboard.writeText(showVerificationInfo.email);
                                    showMessage('Email copied to clipboard!', 'success');
                                }}
                            > COPY EMAIL
                            </button>
                            <button 
                                className="cm-btn cm-btn-secondary" 
                                onClick={() => setShowVerificationInfo(null)}
                            >
                                CLOSE
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ClientAdminManager;
